import { useState, useCallback, useRef } from "react";
import api from "../api/client";

/**
 * Hook that checks for daily team-composition conflicts before saving a record.
 *
 * Usage:
 *   const { check: checkComp, ConflictDialog } = useCompositionGuard();
 *
 *   // In save handler:
 *   const ok = await checkComp({ teamNumber, assignmentDate, personnel1Id, personnel2Id });
 *   if (!ok) return;
 *   // ...actual save...
 *
 *   // In JSX:
 *   return <><ConflictDialog /></>;
 */
export default function useCompositionGuard() {
  const [info, setInfo] = useState(null);
  const resolveRef = useRef(null);

  const check = useCallback(async ({ teamNumber, assignmentDate, personnel1Id, personnel2Id }) => {
    if (!teamNumber || !assignmentDate || !personnel1Id) return true;

    try {
      const params = {
        team_number: teamNumber,
        assignment_date: assignmentDate,
        personnel_1_id: personnel1Id,
      };
      if (personnel2Id) params.personnel_2_id = personnel2Id;

      const { data } = await api.get("/team-masters/composition-conflict", { params });

      if (!data.has_conflict) return true;

      // Show dialog and wait for user decision
      return new Promise((resolve) => {
        resolveRef.current = { resolve, teamNumber, assignmentDate, personnel1Id, personnel2Id };
        setInfo(data);
      });
    } catch {
      return true; // on error, allow the save to proceed
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!resolveRef.current) return;
    const { resolve, teamNumber, assignmentDate, personnel1Id, personnel2Id } = resolveRef.current;
    setInfo(null);
    resolveRef.current = null;

    try {
      const body = {
        team_number: teamNumber,
        assignment_date: assignmentDate,
        personnel_1_id: personnel1Id,
      };
      if (personnel2Id) body.personnel_2_id = personnel2Id;
      await api.post("/team-masters/bulk-reassign-composition", body);
    } catch {
      // best effort
    }
    resolve(true);
  }, []);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current.resolve(false);
      resolveRef.current = null;
    }
    setInfo(null);
  }, []);

  function ConflictDialog() {
    if (!info) return null;
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
          <h3 className="text-lg font-semibold mb-2 text-amber-600">
            Team Composition Changed
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            The selected personnel differ from the current daily assignment.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-sm space-y-1">
            <p>
              <span className="font-medium text-gray-700">Current:</span>{" "}
              {info.current_display || "—"}
            </p>
            <p>
              <span className="font-medium text-gray-700">New:</span>{" "}
              {info.proposed_display || "—"}
            </p>
          </div>
          {info.affected_count > 0 && (
            <p className="text-sm text-gray-600 mb-4">
              This will also update{" "}
              <strong>{info.affected_count}</strong>{" "}
              existing record{info.affected_count !== 1 ? "s" : ""} with the same team for this date.
            </p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 text-sm text-white rounded-lg bg-amber-600 hover:bg-amber-700 transition-colors"
            >
              Update All Records
            </button>
          </div>
        </div>
      </div>
    );
  }

  return { check, ConflictDialog };
}
